import { useReactFlow, XYPosition } from 'reactflow';
import {
  SIDE_PANEL_TRANSITION_TIME,
  VIEWPORT_TRANSITION_TIME,
} from '@constants/canvas/general';
import { LayerTypes, SubLayerTypes } from '@constants/canvas/layers';
import {
  abandonViewportAnimation,
  claimViewportAnimation,
  isLatestViewportAnimation,
  settleViewportAnimation,
} from '@hooks/canvas/viewportAnimation';
import { calculateViewportPosition } from '@utils/canvasHelpers';

/**
 * Brings a node into view, after waiting for the side panel that usually opens
 * alongside it to finish animating.
 *
 * The wait can outlive the component that scheduled it, so the move is skipped
 * rather than thrown from if React Flow has gone away, and the claim on the
 * viewport is released so a later move is not silenced by it.
 */
const useCenterNodeViewport = () => {
  const { setViewport } = useReactFlow();

  return (
    nodePosition: XYPosition,
    nodeLayerId: LayerTypes | SubLayerTypes,
  ) => {
    const token = claimViewportAnimation();

    setTimeout(() => {
      if (!isLatestViewportAnimation(token)) {
        abandonViewportAnimation(token);
        return;
      }

      const position = calculateViewportPosition(nodePosition, nodeLayerId);

      try {
        setViewport(position, { duration: VIEWPORT_TRANSITION_TIME });
        settleViewportAnimation(token);
      } catch {
        abandonViewportAnimation(token);
      }
    }, SIDE_PANEL_TRANSITION_TIME);
  };
};

export default useCenterNodeViewport;
